import React, { FunctionComponent, useState, useEffect } from 'react';
import classnames from 'classnames';
import Loading from '@justgiving/loading';

import { GetStravaSettingsQuery } from '../../../types/__generated__';
import { PageMetaContext } from '../../context/PageMetaContext';
import getStravaSettings from '../StravaSettingsModals/StravaSettingsModal/stravaSettings.gql';
import { useIsFitnessSectionExpanded } from './useIsFitnessSectionExpanded';
import { useTranslation } from 'react-i18next';
import styles from './FitnessSection.scss';
import IconButton from '../IconButton/IconButton';
import { plusIcon, minusIcon } from '@justgiving/icons';
import SVG from '@justgiving/svg';
import { StravaFitnessSection } from '../StravaFitnessSection/StravaFitnessSection';
import Button from '../Button/Button';
import { Tag } from '../Tag/Tag';
import ManualFitnessEntry from '../ManualFitnessEntry/ManualFitnessEntry';
import {
  useFlags,
  useNotNullableContext,
  useFullPageQuery,
  useFitnessConnection,
  useApp,
  useModalToggle,
} from '../../hooks';
import Modal from '../Modal/Modal';
import { StravaModal } from '../StravaSettingsModals/StravaModal';

export const FitnessSection: FunctionComponent = () => {
  const { data: settingsData, loading } = useFullPageQuery<
    GetStravaSettingsQuery
  >(getStravaSettings, {
    context: {
      credentials: 'include',
    },
  });

  const { t } = useTranslation('common', { useSuspense: false });
  const { pageShortName } = useNotNullableContext(PageMetaContext);
  const { virtualFitnessEventsPreview } = useFlags();
  const { isOpen: isModalOpen, setOpen: setModalOpen } = useModalToggle();

  const isConnected = Boolean(
    settingsData?.viewer?.thirdPartyApplications?.strava?.isConnected,
  );

  const isPaused = Boolean(
    isConnected &&
      settingsData?.page?.strava?.settings?.subscribedActivities?.length === 0,
  );

  const hasSettings = Boolean(settingsData?.page?.strava?.settings);

  const { status, error } = useFitnessConnection(isConnected);
  const { startLoading, stopLoading, showToast, hideToast } = useApp();

  useEffect(() => {
    if (status === 'connecting') {
      startLoading();
    } else if (status === 'success') {
      stopLoading();
      localStorage.setItem('strava.initialFormSave', String(1));
      setModalOpen(true);
    } else if (status === 'failed' && error) {
      stopLoading();
      showToast({
        message: error,
        type: 'warning',
        dismissAfter: 10000,
      });
    }

    return () => {
      localStorage.removeItem('strava.initialFormSave');
    };
  }, [status, showToast, stopLoading, startLoading, error, setModalOpen]);

  const getConnectionStatusText = () => {
    if (isPaused) {
      return t('fitnessSection.strava.status.paused');
    }

    if (isConnected) {
      return t('fitnessSection.strava.status.connected');
    }
  };

  const isExpandedByDefault = useIsFitnessSectionExpanded({
    isConnected,
    hasSettings,
    pageShortName,
  });

  const [isExpanded, setIsExpanded] = useState(isExpandedByDefault);

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const handleManageSettingsClick = () => {
    hideToast();
    setModalOpen(true);
  };

  const shouldDisplayAddManualActivityButton =
    virtualFitnessEventsPreview &&
    (settingsData?.page?.totals?.timeline.length === 0 ||
      (settingsData?.page?.strava &&
        !settingsData.page.strava.settings?.showTotaliser));

  if (loading) {
    return <Loading />;
  }

  const hasCookieConsent = document.cookie.includes('JgCookieNotice');

  return (
    <div className={styles.fitnessSection}>
      <div className={styles.headingContainer}>
        <span>
          {!isConnected && <Tag>{t('common.new')}</Tag>}
          <h1 className={`jg-h4 jg-display-ib ${styles.heading}`}>
            {t('fitnessSection.heading')}
          </h1>
          {isConnected && (
            <span
              className={classnames(styles.connectionStatus, 'jg-text--small', {
                [styles.paused]: isPaused,
              })}
            >
              {getConnectionStatusText()}
            </span>
          )}
        </span>
        <IconButton
          aria-label={isExpanded ? t('common.collapse') : t('common.expand')}
          onClick={toggleExpanded}
        >
          <SVG
            markup={isExpanded ? minusIcon : plusIcon}
            className={styles.icon}
          />
        </IconButton>
      </div>
      {isExpanded && (
        <div className={styles.body}>
          <StravaFitnessSection
            isConnected={isConnected}
            isPaused={isPaused}
            className={styles.button}
            onManageSettingsClick={handleManageSettingsClick}
          />
          {shouldDisplayAddManualActivityButton && (
            <ManualFitnessEntry
              opener={({ onOpen }) => (
                <Button outlined className={styles.button} onClick={onOpen}>
                  {t('fitness.addManualActivity')}
                </Button>
              )}
            />
          )}
        </div>
      )}
      {isModalOpen && (
        <Modal
          onCloseRequest={() => setModalOpen(false)}
          scrollIsolation={hasCookieConsent}
        >
          <StravaModal
            connectionStatus={status}
            onClose={() => {
              setModalOpen(false);
            }}
          />
        </Modal>
      )}
    </div>
  );
};
